import { Boxes, Check, Circle, LayoutGrid, Pin, Users } from "lucide-react";
/**
 * First-plan tour: a few coach marks that wait for the real action instead of showing slides.
 *   look around → draw your first road → name it & pick a material → done
 *   (someone who answered the old "what do you want to plan first?" keeps their goal: town → fill it with a layout,
 *   build → a building, explore → look only)
 * Skippable at every step (Esc too); Help → "Replay the tour" starts it again.
 */
import { useEffect, useRef, useState } from "react";
import { KIND_DEFAULTS, type PlanObject } from "@mcp/plan";
import { controller } from "../../app/controller.ts";
import { select, useApp, type Tool } from "../../state/store.ts";
import { prefersReducedMotion, useOnboarding, type Goal } from "../../lib/onboarding.ts";

type StepId = "look" | "draw" | "layout" | "name" | "done";

function stepsFor(goal: Goal): StepId[] {
  if (goal === "explore") return ["look", "done"];
  if (goal === "town") return ["look", "draw", "layout", "done"];
  return ["look", "draw", "name", "done"];
}

const TOOL: Record<Goal, Tool> = { path: "path", town: "area", build: "building", explore: "select" };
const TOOL_NAME: Partial<Record<Tool, string>> = { path: "Path", area: "Area", building: "Building" };

const ob = () => useOnboarding.getState();
const endTour = () => ob().set({ tourStep: null, tourDone: true });

export function Tour() {
  const tourStep = useOnboarding((s) => s.tourStep);
  const goal = useOnboarding((s) => s.goal) ?? "path";
  const phase = useApp((s) => s.phase);

  useEffect(() => {
    if (tourStep === null) return;
    // Esc skips the tour — unless it's cancelling something else first (a drawing, a panel, the help, a text field).
    // Capture phase: we see the state before the drawing tools react to the same key.
    const onKey = (e: KeyboardEvent) => {
      const s = useApp.getState();
      const busy = s.tool !== "select" || s.helpOpen || s.layoutPanel || s.boardFor || s.layout || s.schemPlace || (controller.tools?.draftPoints ?? 0) > 0;
      if (e.key === "Escape" && !busy && !(e.target as HTMLElement)?.closest?.("input,textarea,select")) endTour();
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [tourStep]);

  if (tourStep === null || phase !== "ready") return null;
  const steps = stepsFor(goal);
  const i = Math.min(tourStep, steps.length - 1);
  const next = () => ob().set({ tourStep: i + 1 >= steps.length ? null : i + 1, ...(i + 1 >= steps.length ? { tourDone: true } : {}) });
  const dots = { index: i, total: steps.length };
  switch (steps[i]) {
    case "look": return <LookStep key="look" dots={dots} next={next} />;
    case "draw": return <DrawStep key="draw" dots={dots} next={next} goal={goal} />;
    case "layout": return <LayoutStep key="layout" dots={dots} next={next} />;
    case "name": return <NameStep key="name" dots={dots} next={next} />;
    default: return <DoneStep key="done" dots={dots} />;
  }
}

// ---------------- coach mark ----------------

type Place = "above" | "right" | "left" | "center";
interface Dots { index: number; total: number }

/** position of an element on screen, followed while it moves / appears */
function useRect(selector: string | null) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  useEffect(() => {
    if (!selector) return;
    const read = () => {
      const r = document.querySelector(selector)?.getBoundingClientRect() ?? null;
      setRect((old) => (old && r && old.x === r.x && old.y === r.y && old.width === r.width && old.height === r.height ? old : r));
    };
    read();
    const t = setInterval(read, 200);
    window.addEventListener("resize", read);
    return () => { clearInterval(t); window.removeEventListener("resize", read); };
  }, [selector]);
  return rect;
}

function Coach({ anchor, place, ring, dots, title, children, onNext, nextLabel, wide }: {
  anchor: string; place: Place; ring?: boolean; dots: Dots; title: string; children: React.ReactNode; onNext?: () => void; nextLabel?: string; wide?: boolean;
}) {
  const rect = useRect(anchor);
  const card = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 320, h: 160 });
  useEffect(() => {
    const el = card.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setSize({ w: el.offsetWidth, h: el.offsetHeight }));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  let left = window.innerWidth / 2 - size.w / 2, top = window.innerHeight / 2 - size.h / 2;
  if (rect) {
    // above the bottom of the map, clear of its status row and the welcome toast
    if (place === "above") { left = rect.left + rect.width / 2 - size.w / 2; top = rect.bottom - size.h - 120; }
    else if (place === "right" || place === "left") {
      // beside the anchor; flip to the other side when there's no room
      const rightOk = rect.right + 16 + size.w <= window.innerWidth - 12;
      const leftOk = rect.left - size.w - 16 >= 12;
      const side = place === "right" ? (rightOk || !leftOk ? "right" : "left") : (leftOk || !rightOk ? "left" : "right");
      left = side === "right" ? rect.right + 16 : rect.left - size.w - 16;
      top = place === "right" ? rect.top - 8 : rect.top + 12;
    }
    else { left = rect.left + rect.width / 2 - size.w / 2; top = rect.top + rect.height / 2 - size.h / 2; }
  }
  left = Math.max(12, Math.min(window.innerWidth - size.w - 12, left));
  top = Math.max(12, Math.min(window.innerHeight - size.h - 12, top));
  return (
    <>
      {ring && rect && <div className="tour-ring" style={{ left: rect.left - 5, top: rect.top - 5, width: rect.width + 10, height: rect.height + 10 }} aria-hidden="true" />}
      <div ref={card} className={`tour-card glass-2${wide ? " wide" : ""}`} style={{ left, top }} role="dialog" aria-label={title} aria-live="polite">
        <div className="tour-head">
          <span className="tour-dots" role="img" aria-label={`Step ${dots.index + 1} of ${dots.total}`}>
            {Array.from({ length: dots.total }, (_, k) => <i key={k} className={k < dots.index ? "done" : k === dots.index ? "now" : ""} />)}
          </span>
          <span className="tour-count tabular" aria-hidden="true">{dots.index + 1} of {dots.total}</span>
          <button type="button" className="btn btn-sm btn-ghost tour-skip" onClick={endTour}>Skip tour</button>
        </div>
        <h3>{title}</h3>
        {children}
        {onNext && <div className="tour-actions"><button type="button" className={`btn btn-sm ${nextLabel && nextLabel !== "Next" ? "btn-ghost" : "btn-primary"}`} onClick={onNext}>{nextLabel ?? "Next"}</button></div>}
      </div>
    </>
  );
}

// ---------------- steps ----------------

function LookStep({ dots, next }: { dots: Dots; next: () => void }) {
  const [moved, setMoved] = useState(false);
  const [zoomed, setZoomed] = useState(false);
  useEffect(() => {
    let start: ReturnType<NonNullable<typeof controller.engine>["view"]> | null = null;
    const t = setInterval(() => {
      const e = controller.engine;
      if (!e || e.flying) return; // wait until the fly-in has landed
      const v = e.view();
      start ??= v;
      if (Math.hypot(v.x - start.x, v.z - start.z) > 10) setMoved(true);
      if (Math.abs(v.dist / start.dist - 1) > 0.18) setZoomed(true);
    }, 200);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    if (!moved || !zoomed) return;
    const t = setTimeout(next, 700);
    return () => clearTimeout(t);
  }, [moved, zoomed]);
  return (
    <Coach anchor=".viewport" place="above" dots={dots} title="Have a look around" onNext={next} nextLabel={moved && zoomed ? "Next" : "Skip this"}>
      <p>Drag the map to move and scroll to zoom. Right-drag turns the view.</p>
      <p className="tour-keys">Keyboard: <kbd>W</kbd><kbd>A</kbd><kbd>S</kbd><kbd>D</kbd> move · <kbd>Q</kbd><kbd>E</kbd> turn · <kbd>+</kbd><kbd>−</kbd> zoom</p>
      <ul className="tour-checks">
        <li className={moved ? "ok" : ""}>{moved ? <Check className="lead" size={14} aria-hidden="true" /> : <Circle className="lead" size={14} aria-hidden="true" />}Move</li>
        <li className={zoomed ? "ok" : ""}>{zoomed ? <Check className="lead" size={14} aria-hidden="true" /> : <Circle className="lead" size={14} aria-hidden="true" />}Zoom</li>
      </ul>
    </Coach>
  );
}

function DrawStep({ dots, next, goal }: { dots: Dots; next: () => void; goal: Goal }) {
  const tool = TOOL[goal];
  const current = useApp((s) => s.tool);
  const plans = useApp((s) => s.plans);
  const [drafting, setDrafting] = useState(false);
  useEffect(() => {
    const t = setInterval(() => setDrafting((controller.tools?.draftPoints ?? 0) > 0), 150);
    return () => clearInterval(t);
  }, []);
  const before = useRef<Set<string> | null>(null);
  useEffect(() => {
    before.current = new Set(useApp.getState().plans.map((p) => p.id));
    select([]);
    useApp.getState().set({ tool });
  }, []);
  useEffect(() => {
    if (!before.current) return;
    const made = plans.find((p) => !before.current!.has(p.id));
    if (made) {
      ob().set({ tourObject: made.id });
      next();
    }
  }, [plans]);
  const body = tool === "path"
    ? <>Click on the map to set points along your road. <kbd>Enter</kbd> or a double-click finishes it.</>
    : tool === "area"
      ? <>Click the corners of the land for your town. Click the first corner again (or press <kbd>Enter</kbd>) to close it.</>
      : <>Click one corner of your build, then <kbd>Shift</kbd>+click the opposite corner for a rectangle.</>;
  return (
    <>
      <Coach anchor={`[data-tour="tool-${tool}"]`} place="right" ring dots={dots} title={`Draw your first ${goal === "town" ? "town" : goal === "build" ? "build" : "road"}`}>
        <p>{body}</p>
        {current !== tool && <p><button type="button" className="link" onClick={() => useApp.getState().set({ tool })}>Pick the {TOOL_NAME[tool]} tool again</button></p>}
        <p className="tour-keys">Made a mistake? <kbd>Backspace</kbd> removes the last point, <kbd>Esc</kbd> starts over.</p>
      </Coach>
      {!drafting && current === tool && <div className="tour-ghost" aria-hidden="true"><span>Click here to start</span></div>}
    </>
  );
}

function LayoutStep({ dots, next }: { dots: Dots; next: () => void }) {
  const id = useOnboarding((s) => s.tourObject);
  const plans = useApp((s) => s.plans);
  const panel = useApp((s) => s.layoutPanel);
  const before = useRef<number>(0);
  useEffect(() => {
    before.current = useApp.getState().plans.filter((p) => p.group).length;
    if (id) controller.startFill(id);
  }, []);
  useEffect(() => {
    if (plans.filter((p) => p.group).length > before.current) next();
  }, [plans]);
  return (
    <Coach anchor=".layout-panel" place="left" ring={!!panel} dots={dots} title="Fill it with a town">
      <p>Pick a preset (Grid city, Village, Round town) and try the <b>Size</b> slider. You see a preview on the map. Happy? Press <b>Create</b>.</p>
      {!panel && id && <p><button type="button" className="link" onClick={() => controller.startFill(id)}>Open the layouts again</button></p>}
      <p className="tour-keys">It's all one step: <kbd>Ctrl</kbd><kbd>Z</kbd> takes it back.</p>
    </Coach>
  );
}

function NameStep({ dots, next }: { dots: Dots; next: () => void }) {
  const id = useOnboarding((s) => s.tourObject);
  const obj = useApp((s) => s.plans.find((p) => p.id === id) ?? null);
  const start = useRef<PlanObject | null>(null);
  useEffect(() => {
    if (id) select([id]);
  }, []);
  useEffect(() => {
    if (!obj) return;
    start.current ??= obj;
    const s = start.current;
    const renamed = obj.name !== s.name && obj.name.trim() !== "" && !obj.name.startsWith(KIND_DEFAULTS[obj.kind].name + " ");
    if (renamed || obj.material !== s.material) {
      const t = setTimeout(next, 900);
      return () => clearTimeout(t);
    }
  }, [obj]);
  return (
    <Coach anchor='[data-tour="inspector"]' place="left" ring dots={dots} title="Make it yours" onNext={next} nextLabel="Skip this">
      <p>Give it a name, and pick what it's made of: gravel, stone bricks, planks… Set its status as you build: idea, then planned, then done.</p>
    </Coach>
  );
}

function DoneStep({ dots }: { dots: Dots }) {
  const id = useOnboarding((s) => s.tourObject);
  const [burst, setBurst] = useState<{ x: number; y: number } | null>(null);
  useEffect(() => {
    if (prefersReducedMotion()) return;
    const o = id ? useApp.getState().plans.find((p) => p.id === id) : null;
    const vp = document.querySelector(".viewport")?.getBoundingClientRect();
    if (!vp) return;
    let at = { x: vp.left + vp.width / 2, y: vp.top + vp.height / 2 };
    if (o?.points.length) {
      const cx = o.points.reduce((a, p) => a + p[0], 0) / o.points.length;
      const cz = o.points.reduce((a, p) => a + p[1], 0) / o.points.length;
      const s = controller.screenPos(cx, (controller.engine?.heights.atOr(cx, cz, o.y) ?? o.y) + 2, cz);
      if (s) at = { x: vp.left + s.x, y: vp.top + s.y };
    }
    setBurst(at);
    const t = setTimeout(() => setBurst(null), 1100);
    return () => clearTimeout(t);
  }, []);
  const go = (f: () => void) => { endTour(); f(); };
  const hasObject = !!id && useApp.getState().plans.some((p) => p.id === id);
  return (
    <>
      {burst && <Burst x={burst.x} y={burst.y} />}
      <Coach anchor=".viewport" place="center" dots={dots} wide title={hasObject ? "That's your first plan." : "You're all set."}>
        <p>A few things to try next:</p>
        <div className="tour-next">
          <button type="button" onClick={() => go(() => controller.openLayouts("fill"))}><LayoutGrid size={16} aria-hidden="true" /><span><b>Layouts</b><span>Streets and plots in one go</span></span></button>
          <button type="button" onClick={() => go(() => controller.openLayouts("schematics"))}><Boxes size={16} aria-hidden="true" /><span><b>Schematics</b><span>Place real builds</span></span></button>
          <button type="button" onClick={() => go(() => { if (id && hasObject) { select([id]); controller.openBoard(id); } })} disabled={!hasObject}><Pin size={16} aria-hidden="true" /><span><b>Pinboard</b><span>Ideas, pictures, links</span></span></button>
          <button type="button" onClick={() => go(() => (document.querySelector(".collab-btn") as HTMLButtonElement | null)?.click())}><Users size={16} aria-hidden="true" /><span><b>Plan with friends</b><span>Share an invite link</span></span></button>
        </div>
        <div className="tour-actions"><button type="button" className="btn btn-sm btn-primary" autoFocus onClick={endTour}>Start planning</button></div>
      </Coach>
    </>
  );
}

function Burst({ x, y }: { x: number; y: number }) {
  const bits = Array.from({ length: 14 }, (_, i) => {
    const a = (i / 14) * Math.PI * 2 + (i % 2) * 0.3;
    const d = 46 + (i % 3) * 22;
    return { dx: Math.cos(a) * d, dy: Math.sin(a) * d - 18, c: ["var(--accent-2)", "var(--accent)", "var(--amber)", "var(--tint-sky)"][i % 4] };
  });
  return (
    <div className="tour-burst" style={{ left: x, top: y }} aria-hidden="true">
      {bits.map((b, i) => <i key={i} style={{ "--dx": `${b.dx}px`, "--dy": `${b.dy}px`, background: b.c } as React.CSSProperties} />)}
    </div>
  );
}
